import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableProps,
  Switch,
  Space,
  ConfigProvider,
  theme,
  Select,
  Tag,
  Button as AntdButton,
  message,
} from "antd";
import { Input } from "@/components/ui/inputForm";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  ativarDesativarCategoriaProduto,
  getAllCategoriasProduto,
} from "@/services/categoriaProduto";
import { errorHandler } from "@/utils/errorhandler";
import { usePermissions } from "@/hooks/usePermissions";

const MENU_ID_CATEGORIA_PRODUTO = 31;

export default function ConsultaCategoriaProduto() {
  const [categorias, setCategorias] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    handleGetCategorias();
  }, []);

  const handleGetCategorias = async () => {
    try {
      const response = await getAllCategoriasProduto();
      if (response.status === 200) {
        setCategorias(response.data);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleAdd = useCallback(() => {
    navigate("/categoria-produto/cadastro");
  }, [navigate]);

  const handleEdit = (id: number) => {
    navigate(`/categoria-produto/${id}`);
  };

  const toggleAtivo = async (categoria: any) => {
    try {
      await ativarDesativarCategoriaProduto(categoria.id, !categoria.ativo);
      setCategorias((prev) =>
        prev.map((c) => (c.id === categoria.id ? { ...c, ativo: !c.ativo } : c))
      );
      message.success(`Categoria ${!categoria.ativo ? "ativada" : "inativada"} com sucesso!`);
    } catch (error) {
      errorHandler(error);
    }
  };

  const filteredCategorias = categorias.filter((c) => {
    const matchesSearch = c.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && c.ativo) ||
      (statusFilter === "inativos" && !c.ativo);
    return matchesSearch && matchesStatus;
  });

  const columns: TableProps<any>["columns"] = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Controle de Entregas",
      dataIndex: "controleEntregas",
      key: "controleEntregas",
      render: (value) => (
        <Tag color={value ? "green" : "default"}>{value ? "Sim" : "Não"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value, record) => (
        <Switch
          checked={!!value}
          onChange={() => toggleAtivo(record)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
          disabled={!canEdit(MENU_ID_CATEGORIA_PRODUTO)}
        />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, record) => (
        <Space size="middle">
          {canEdit(MENU_ID_CATEGORIA_PRODUTO) && (
            <AntdButton
              type="text"
              icon={<EditOutlined />}
              title="Editar categoria"
              onClick={() => handleEdit(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Categorias de Produtos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativos", label: "Ativos" },
              { value: "inativos", label: "Inativos" },
            ]}
          />
          {canInsert(MENU_ID_CATEGORIA_PRODUTO) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredCategorias} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
}
